import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { invalidateGenreProducts, useGenreProducts } from './api/products/useGenreProducts';
import { useCollectionGenres } from './api/genres/useCollectionGenres';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../store/appState';
import { ProductMetaData, ProductSorting } from '../../../../models/Product';
import { ContainerInnerWithColumns } from '../../../shared/common/ContainerInner';
import { FilterAndProductListWrapper } from '../../../shared/common/FilterAndProductListWrapper';
import { LoadingWrapper } from '../../../shared/components/LoadingWrapper';
import LoadingOverlay from '../../../shared/components/LoadingOverlay';
import { SidebarContainer } from '../../../shared/common/SidebarContainer';
import { GenreSidebar } from './GenreSidebar';
import styled from 'styled-components';
import ProductList from '../../../shared/common/ProductList';
import { useHistory, useLocation, useParams } from 'react-router';
import { FilterBar } from '../../../shared/common/FilterBar';
import { displayByGenre } from '../../../store/header/actions';
import { Header } from './Header';
import {
    deselectAll,
    deselectOne,
    isInSelectionMode,
    isSelected,
    selectAll,
    selectOne,
    setSelectionMode
} from '../../../store/productSelection/actions';
import { BreadcrumbsState } from '../../../store/breadcrumbs/state';
import Pagination, { getPaginationStateObject } from '../../../shared/common/Pagination';
import ListItem from '../../../shared/common/ListItems/ListItem';
import { useQueryParams } from '../../../shared/hooks/useQueryParams';
import { modifyURLState, modifyURLStates } from '../../../shared/utils/utils-routing';
import NoElementsFound, { NO_ELEMENTS_PRODUCT } from '../../../shared/components/NoElementsFound';
import queryString from 'query-string';
import { AgeCategory, ageCategoryFromString } from '../../../../models/AgeCategory';
import { deserializeFilter } from '../../../shared/utils/deserializeFilter';
import { getLoanFormat } from '../../../shared/utils/getLoanFormat';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { fetchByGenreAggregationsQuickSearch } from '../../../api/collection/your-collection/by-genre/fetchByGenreAggregationsQuicksearch';
import { useLibraryManagedProductGroups } from '../../marketing/productgroups/api/useLibraryManagedProductGroupsFeaturePages';
import { AuthenticationState } from '../../../store/authentication/state';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { fetchByGenreAggregations } from '../../../api/collection/your-collection/by-genre/fetchByGenreAggregations';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';
import { addProductsToGroup } from '../../../api/library-managed/addProductsToGroup';
import { CollectionProductTile } from '../CollectionProductTile';
import { setGenre } from '../../../store/collection/actions';
import { useAddBulkFromCollectionToCart } from '../../../api/shopping-cart/useAddFromCollectionToCart';
import { SOURCE_COLLECTION_BY_GENRE } from '../../../store/constants';
import { convertSelectionState } from '../../../shared/product-selection/ProductSelectionTypes';
import { Orderlist } from '../../../../models/OrderList';
import { useAddBulkFromCollectionToList } from '../../../api/selection-list/useAddFromCollectionToList';
import { Article } from '../../../../models/Article';
import { CopyDetailsPopup } from '../copyDetailsPopup/CopyDetailsPopup';
import { useAddToList } from '../../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../../api/shopping-cart/useAddToCart';
import { SingleAddOptions } from '../../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../../models/ListActionTarget';
import { useListContentChecks } from '../../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../../shared/common/CheckBeforeAddingModal';

const FullWidthContainer = styled.div<{ isLoading: boolean }>`
    width: 100%;
    min-height: ${props => (props.isLoading ? '100vh' : '')};
`;

const FilterBarWithSpace = styled(FilterBar)`
    padding: 0 0 25px 0;
`;

type GenreParams = { genre?: string; subgenre?: string };

const getSelectedGenre = ({ genre, subgenre }: GenreParams) =>
    genre ? decodeURIComponent(genre) + (subgenre ? `->${decodeURIComponent(subgenre)}` : '') : undefined;

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';

export const DEFAULT_PAGE_SIZE = 54;

export const CollectionByGenrePage: FC = () => {
    const history = useHistory();
    const { search } = useLocation();
    const [selectedProduct, setSelectedProduct] = useState<(ProductMetaData & Article) | undefined>(undefined);

    const dispatch = useDispatch();
    const authentication = useSelector((state: AppState) => state.authentication);
    const productSelection = useSelector((state: AppState) => state.productSelection);
    const isSticky = useSelector((state: AppState) => state.header.isSticky);

    useLayoutEffect(() => {
        dispatch(displayByGenre());
    }, [dispatch]);

    const { genre, subgenre, loanFormat: loanFormatPathParam } = useParams<GenreParams & { loanFormat?: string }>();
    const loanFormat = getLoanFormat(loanFormatPathParam);

    /**
     * The `displayFilter` query param is either there or not. There is no value attached to it.
     * Examples:
     *      with:       `/collection/by-genre/all?displayFilter
     *      without:    `/collection/by-genre/all
     *
     * Parsing this query param with the `query-string` library will result in either null value or undefined.
     * This is the way how we can identify if the filter should be open or not.
     * Unfortunately, we can not replicate this behaviour with the `useQueryParams` hook.
     *
     * Of course it would be nicer to have a boolean value.
     * But that is a breaking change in regard to the URL structure.
     */
    const { displayFilter: displayFilterQueryParam } = queryString.parse(search);
    const displayFilter = displayFilterQueryParam === null;

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },
        filter: { value: filterQueryParam },
        ageCategory: { value: ageCategoryQueryParam },
        display: { value: display }
    } = useQueryParams({
        [PAGE_QUERY_PARAM]: 'integer',
        [SORT_BY_QUERY_PARAM]: 'string',
        pageSize: 'integer',
        filter: 'string',
        ageCategory: 'string',
        display: 'string'
    });
    const page = pageParam - 1 ?? 0;
    const sortBy = sortByParam ?? ProductSorting.RELEASE_DATE_NEW_TO_OLD;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const ageCategory = ageCategoryFromString(ageCategoryQueryParam);
    const filter = deserializeFilter(filterQueryParam);

    const {
        data: collectionGenres,
        isLoading: isCollectionGenresLoading,
        isSuccess: isCollectionGenresSuccess,
        defaultGenre,
        hasGenres
    } = useCollectionGenres({
        ageCategory,
        loanFormat
    });

    const selectedGenre = getSelectedGenre({ genre, subgenre }) ?? defaultGenre;

    useEffect(() => {
        dispatch(setGenre(selectedGenre));
    }, [dispatch, selectedGenre]);

    const { data: genreProducts, isLoading: isProductsLoading } = useGenreProducts({
        ageCategory,
        loanFormat: loanFormat.requestKey,
        genre: selectedGenre,
        filterMap: filter,
        pagination: { sortBy, page, pageSize }
    });

    const productGroups = useLibraryManagedProductGroups();

    const loading = isCollectionGenresLoading || isProductsLoading;

    const products = genreProducts?.hits ?? [];
    const totalProducts = genreProducts?.totalHits ?? 0;

    const breadcrumbs: BreadcrumbsState = {
        key: 'by-genre',
        label: 'By Genre',
        buckets: collectionGenres?.buckets || [],
        activeBucket: selectedGenre
    };

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => byGenrePageFilterGroups(authentication, ageCategory),
        [ageCategory, authentication]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'byGenrePage',
            loanFormat.requestKey!,
            'aggregation',
            { ageCategory, genre: selectedGenre, subgenre },
            filters,
            expandedFilterGroups
        ],
        [ageCategory, loanFormat.requestKey, selectedGenre, subgenre]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options =>
            fetchByGenreAggregations({
                ...options,
                loanFormat: loanFormat.requestKey!,
                ageCategory,
                genre: selectedGenre,
                subgenre
            }),
        [ageCategory, loanFormat.requestKey, selectedGenre, subgenre]
    );
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options =>
            fetchByGenreAggregationsQuickSearch({
                ...options,
                ageCategory,
                genre: selectedGenre,
                subgenre,
                loanFormat: loanFormat.requestKey!
            }),
        [ageCategory, loanFormat.requestKey, selectedGenre, subgenre]
    );
    //endregion

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: SORT_BY_QUERY_PARAM, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(history.location.search, [pageQueryState, sortByQueryState]);

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const setPage = nextPage => {
        const nextSearch = modifyURLState(
            history.location.search,
            PAGE_QUERY_PARAM,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(history.location.search, 'displayFilter', !displayFilter, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function onDisplayChange(nextDisplayType) {
        const nextSearch = modifyURLState(history.location.search, 'display', nextDisplayType, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function handleProductSelection(productId) {
        const selected = isSelected(productId, productSelection);
        if (!selected) {
            dispatch(selectOne(productId));
        } else {
            dispatch(deselectOne(productId));
        }
    }

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart(() => {
        invalidateGenreProducts().then();
    });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps } = useListContentChecks<SingleAddOptions>({ onAction });

    const { isLoading: isLoadingAddProductsToCart, addToCartBulk } = useAddBulkFromCollectionToCart({
        loanFormat: loanFormat,
        filter: filterQueryParam ?? '',
        ageCategory,
        source: SOURCE_COLLECTION_BY_GENRE,
        genreId: selectedGenre,
        callback: () => {
            invalidateGenreProducts().then();
        }
    });

    const { isLoading: isLoadingAddProductsToList, addToListBulk } = useAddBulkFromCollectionToList({
        loanFormat: loanFormat,
        filter: filterQueryParam ?? '',
        ageCategory,
        source: SOURCE_COLLECTION_BY_GENRE,
        genreId: selectedGenre
    });

    const onBulkAddToCart = async (performChecks: boolean) => {
        return addToCartBulk(performChecks, convertSelectionState(productSelection, totalProducts));
    };

    const onBulkAddToList = async (orderlist: Orderlist, performChecks: boolean) => {
        return addToListBulk(orderlist.id, performChecks, convertSelectionState(productSelection, totalProducts));
    };

    if (isCollectionGenresSuccess && !hasGenres) {
        return <NoElementsFound type={NO_ELEMENTS_PRODUCT} />;
    }

    const breadcrumbNavLinkParams = `?${queryString.stringify({
        ageCategory,
        display,
        diplayFilter: displayFilterQueryParam
    })}`;

    return (
        <ContainerInnerWithColumns>
            <LoadingWrapper>
                <LoadingOverlay loading={loading} />
            </LoadingWrapper>

            {selectedProduct ? (
                <CopyDetailsPopup
                    onClosePopup={() => setSelectedProduct(undefined)}
                    onAddPrice={changePrice}
                    product={selectedProduct}
                />
            ) : null}

            <Header
                loanFormat={loanFormat}
                productSelectionCount={productSelection.count}
                isInSelectionMode={isInSelectionMode(productSelection)}
                isSticky={isSticky || false}
                cancelSelection={() => dispatch(setSelectionMode(false))}
                onBulkAddToCart={onBulkAddToCart}
                onBulkAddToGroup={productGroupId => dispatch(addProductsToGroup(productGroupId))}
                onBulkAddToList={onBulkAddToList}
                productGroups={productGroups}
                deselectAll={() => dispatch(deselectAll())}
                breadcrumbs={breadcrumbs}
                topGenre={
                    collectionGenres?.topBuckets && collectionGenres.topBuckets.length > 0
                        ? collectionGenres?.topBuckets[0].key
                        : undefined
                }
                queryParamString={breadcrumbNavLinkParams}
            />

            <FilterBarWithSpace
                totalProducts={totalProducts}
                onSortChange={setSortBy}
                dispatch={dispatch}
                productSelection={productSelection}
                currentPage={page}
                pageSize={pageSize}
                onPageChange={setPage}
                hideGroupBy={true}
                displayType={display}
                sortBy={sortBy}
                onFilterDisplayToggle={onFilterDisplayToggle}
                onDisplayChange={onDisplayChange}
                selectAll={() => dispatch(selectAll(totalProducts))}
                toggleSelectionMode={() => dispatch(setSelectionMode(!isInSelectionMode(productSelection)))}
                displayFilter={displayFilter}
            />
            <FilterAndProductListWrapper>
                {displayFilter ? (
                    <ResponsiveFilterBar
                        visibleFilterGroups={visibleFilterGroups}
                        buildQueryKey={aggregationsQueryKeyBuilder}
                        fetchAggregations={fetchAggregations}
                        fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                    />
                ) : (
                    <SidebarContainer className="sidebar">
                        {collectionGenres && selectedGenre && loanFormat ? (
                            <GenreSidebar
                                genres={collectionGenres}
                                selectedGenre={selectedGenre}
                                loanFormat={loanFormat}
                            />
                        ) : null}
                    </SidebarContainer>
                )}
                <FullWidthContainer
                    isLoading={
                        isProductsLoading ||
                        isCollectionGenresLoading ||
                        isLoadingAddProductsToCart ||
                        isLoadingAddProductsToList
                    }
                >
                    {!loading && totalProducts > 0 ? (
                        <ProductList
                            products={products}
                            totalProducts={totalProducts}
                            displayType={display}
                            productSelection={productSelection}
                            onSelectProduct={handleProductSelection}
                            onAddPrice={changePrice}
                            showManagePopup={(product: ProductMetaData & Article) => setSelectedProduct(product)}
                            tileItemComponent={CollectionProductTile}
                            listItemComponent={ListItem}
                            currentPage={page}
                        />
                    ) : null}
                    {!loading && totalProducts === 0 ? <NoElementsFound type={NO_ELEMENTS_PRODUCT} /> : null}

                    {!loading ? (
                        <div style={{ margin: '40px 0' }} className="bottom-pagination">
                            <Pagination
                                totalItems={totalProducts}
                                onPageChange={setPage}
                                currentPage={page}
                                pageSize={pageSize}
                            />
                        </div>
                    ) : null}
                </FullWidthContainer>
            </FilterAndProductListWrapper>
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
        </ContainerInnerWithColumns>
    );
};

const byGenrePageFilterGroups = (authentication: AuthenticationState, ageCategory?: AgeCategory) => [
    ...defaultVisibleFilterGroups(authentication, ageCategory),
    'stockUsageCondition'
];
